import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, History, Loader2, MessageSquarePlus, PanelLeftClose, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

export const ChatSidebar = ({
  open, onClose,
  sessions, sessionsLoading,
  activeSessionId,
  onNewChat, onLoadSession, onDeleteSession,
}) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: 280, opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="flex-shrink-0 overflow-hidden border-r border-[var(--isp-glass-border)]"
      >
        <div className="w-[280px] h-full flex flex-col bg-[var(--isp-glass-bg)] backdrop-blur-xl">
          <div className="p-3 border-b border-[var(--isp-glass-border)] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2">
              <History className="w-4 h-4" /> Chat History
            </h3>
            <Button
              variant="ghost" size="sm"
              onClick={onClose}
              className="rounded-lg p-1 text-[var(--isp-muted)]"
              data-testid="close-sidebar"
            >
              <PanelLeftClose className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-2">
            <Button
              onClick={onNewChat}
              className="w-full rounded-xl gap-2 text-sm bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700"
              data-testid="new-chat-btn"
            >
              <MessageSquarePlus className="w-4 h-4" /> New Chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessionsLoading && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-[var(--isp-muted)]" />
              </div>
            )}
            {!sessionsLoading && sessions.length === 0 && (
              <p className="text-xs text-[var(--isp-muted)] text-center py-4">No chat history yet</p>
            )}
            {!sessionsLoading && sessions.length > 0 && sessions.map(session => {
              const isActive = activeSessionId === session.id;
              const cls = isActive
                ? 'bg-purple-500/20 border border-purple-500/30'
                : 'hover:bg-[var(--isp-glass-inner)] border border-transparent';
              return (
                <div
                  key={session.id}
                  className={`group flex items-center gap-2 p-2.5 rounded-xl cursor-pointer transition-all text-left w-full ${cls}`}
                  onClick={() => onLoadSession(session.id)}
                  data-testid={`session-${session.id}`}
                >
                  <Brain className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--isp-heading)] truncate font-medium">{session.title}</p>
                    <p className="text-[10px] text-[var(--isp-muted)]">{session.message_count || 0} msgs</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteSession(session.id); }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-red-400 hover:bg-red-500/20 transition-all"
                    data-testid={`delete-session-${session.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
